export interface CircularSummary {
  id: string
  code: string
  stateCode: string
  stateName: string
  lobCode: string
  status: 'approved' | 'proposal' | 'na'
  /** The line's name - the record's own LBL_FULL where it states one. */
  lobName: string
  /** How the feed names the action, shown small above the title. */
  sourceTag: string | null
  /** What the feed calls the figure - "rate change", "loss cost change". */
  circularLabel: string | null
  /** The feed's two-line summary, shown under the title. */
  summaryLine: string | null
  statusLabel: string
  /** Which of the three chip colours the word gets. */
  statusTone: string
  /** The feed's own DOCUMENT_TYPE, shown as the tag beside the bureau. */
  documentType: string | null
  bureauCode: string
  bureauLabel: string
  kind: string
  subject: string
  note: string
  publishedAt: string
  publishedPrecision: 'day' | 'month'
  publishedDisplay: string
  /** Null where no document set an effective date. */
  effectiveDate: string | null
  /** The dash when `effectiveDate` is null. */
  effectiveDisplay: string
  rateChangeDisplay: string
  rateChangePct: string | null
  rateTone: string
  rateWord: string | null
  isProposal: boolean
  sourceUrl: string | null
  hasAnalysis: boolean
  ordinal: number
  /** The routing template this bulletin belongs to (Alert routing's own names). */
  bulletinTypeKey: string | null
  bulletinTypeLabel: string | null
  bulletinTypeBinding: boolean | null
}

export interface CircularAnalysis {
  trackingNumber: string
  /** Null where the feed states no severity. */
  priority: 'high' | 'medium' | 'low' | null
  /** Null where the feed states no pill. */
  badge: 'mandatory' | 'review' | null
  /** The feed's own pill, verbatim - what the chip prints when there is one. */
  badgeLabel: string | null
  sourceAuthority: string
  deadlineDays: number | null
  deadlineLabel: string
  documentType: string
  exposureBase: string
  indicationDisplay: string
  executiveSummary: string | null
  context: string | null
  filingDeadline: string | null
  serffTracking: string | null
  requirement: string | null
  stateTracking: string | null
  complianceBanner: string | null
  hasTabs: boolean
}

export interface SourceDoc { id: string; title: string; url: string; recordLabel: string; ordinal: number }
export interface ImpactStat { id: string; key: string; value: string; tone: string; ordinal: number }
export interface TrendRow { id: string; label: string; value: string; tone: string; ordinal: number }
export interface ClassRow { id: string; classCode: string; classification: string; change: string; ordinal: number }
export interface CircularFormRow { id: string; code: string; name: string; note: string; ordinal: number }
export interface StepRow { id: string; ordinal: number; task: string; ownerDepartment: string }
export interface DeliveryTag { id: string; kind: string; channel: string | null; tone: string; text: string; icon: string; ordinal: number }
export interface CircularMessage { id: string; role: 'user' | 'assistant'; body: string; createdAt: string }

/** This carrier's own exposure to a circular, computed from the portfolio. */
export interface DerivedImpact {
  available: boolean
  message: string | null
  basis: string | null
  stats: { key: string; value: string; tone: string }[]
}

/** A list of objects from the record, as columns and rows. */
export interface RecordTable {
  columns: string[]
  rows: string[][]
}

export interface CircularDetail extends CircularSummary {
  /** The record's own classification list; columns come from its keys. */
  classTable: RecordTable | null
  analysis: CircularAnalysis | null
  derivedImpact: DerivedImpact | null
  /** Written from the record when no analysis was hand-written for this circular. */
  derivedSummary: string | null
  derivedFiling: { key: string; value: string; tone: string }[]
  derivedSourceAuthority: string | null
  derivedDocumentType: string | null
  derivedBadge: string | null
  derivedTiming: string | null
  sourceDocs: SourceDoc[]
  impactStats: ImpactStat[]
  trendRows: TrendRow[]
  classRows: ClassRow[]
  forms: CircularFormRow[]
  steps: StepRow[]
  deliveryTags: DeliveryTag[]
  messages: CircularMessage[]
}

export const TONE_VAR: Record<string, string> = {
  primary: 'var(--ix-primary)',
  success: 'var(--ix-success)',
  warning: 'var(--ix-warning)',
  error: 'var(--ix-error)',
  info: 'var(--ix-info)',
  muted: 'var(--ix-text-muted)',
  secondary: 'var(--ix-text-secondary)',
  text: 'var(--ix-text-primary)',
}

export const TONE_TINT: Record<string, string> = {
  primary: 'var(--ix-primary-tint)',
  success: 'var(--ix-success-tint)',
  warning: 'var(--ix-warning-tint)',
  error: 'var(--ix-error-tint)',
  info: 'var(--ix-info-tint)',
}
